/**
 * PageTransition.js
 *
 * Smooth page-to-page fade using a full-screen navy overlay.
 *
 * On load:    overlay (opacity 1) fades out → page is revealed.
 * On navigate: overlay fades in → browser navigates.
 * On bfcache restore (back/forward): overlay fades out again.
 *
 * Hook: .js-page-transition
 */

const FADE_OUT_MS = 420; // time for overlay to disappear on page arrive
const FADE_IN_MS  = 320; // time for overlay to appear before navigating

export default class PageTransition {
	constructor() {
		this.overlay = document.querySelector( '.js-page-transition' );
		if ( ! this.overlay ) return;

		this._bindEvents();
		this._reveal();
	}

	// Fade the overlay out — called on arrival / bfcache restore.
	_reveal() {
		// Double rAF ensures the browser has painted the overlay at opacity:1
		// before we start the transition to opacity:0.
		requestAnimationFrame( () => {
			requestAnimationFrame( () => {
				this.overlay.classList.add( 'is-visible' );
				// 'is-visible' triggers CSS transition to opacity:0
			} );
		} );
	}

	_bindEvents() {
		document.addEventListener( 'click', ( e ) => this._onClick( e ) );

		// bfcache: page restored from back/forward cache — re-run reveal.
		window.addEventListener( 'pageshow', ( e ) => {
			if ( e.persisted ) {
				this.overlay.classList.remove( 'is-visible', 'is-leaving' );
				this._reveal();
			}
		} );
	}

	_onClick( e ) {
		const link = e.target.closest( 'a[href]' );
		if ( ! link ) return;

		const href = link.getAttribute( 'href' );

		// Skip anchors, mailto, tel, external, and new-tab links.
		if (
			! href ||
			href.startsWith( '#' ) ||
			href.startsWith( 'mailto:' ) ||
			href.startsWith( 'tel:' ) ||
			link.target === '_blank'
		) return;

		try {
			const url = new URL( href, location.href );
			if ( url.origin !== location.origin ) return;
			// Skip if same page (e.g. anchor-only hash change)
			if ( url.pathname === location.pathname && url.search === location.search ) return;
		} catch {
			return;
		}

		e.preventDefault();
		this._leave( link.href );
	}

	_leave( href ) {
		this.overlay.classList.remove( 'is-visible' );
		this.overlay.classList.add( 'is-leaving' );

		setTimeout( () => {
			window.location.href = href;
		}, FADE_IN_MS );
	}
}
